/**
 * @fileoverview O filtro por pessoa do histórico: um campo com busca pelo nome
 * ou e-mail, feita pelo servidor (a equipe pode ter centenas de pessoas, e as
 * fotos pesam). Aberto sem texto, mostra as 30 primeiras por nome e avisa se
 * há mais; digitando, as 30 que batem. Cada opção traz a foto (ou as
 * iniciais), o nome e o e-mail, como a lista de Pessoas; a escolhida mostra a
 * foto no campo. Limpar volta para todas as pessoas.
 */

import React, { useEffect, useMemo, useState } from "react";
import { Paper, TextField } from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { useDebounce } from "../../hooks/useDebounce";
import { i18n } from "../../translate/i18n";
import PersonAvatar from "./PersonAvatar";
import useTeamAccessStyles from "./styles";

const t = (key, options) => i18n.t(`accessControl.${key}`, options);

const SEARCH_DELAY_MS = 300;

const PersonFilter = ({ value, onChange }) => {
  const classes = useTeamAccessStyles();
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [result, setResult] = useState({ members: [], total: 0, loading: false });
  const search = useDebounce(text, SEARCH_DELAY_MS).trim();
  // Com uma pessoa escolhida, o campo mostra o nome dela: não é uma busca.
  const term = value && search === value.name ? "" : search;

  // Busca só com a lista aberta.
  useEffect(() => {
    if (!open) return undefined;
    let active = true;
    setResult((prev) => ({ ...prev, loading: true }));
    api
      .get("/access-control/members", { params: { search: term || undefined } })
      .then(({ data }) => {
        if (active) setResult({ members: data.members, total: data.total, loading: false });
      })
      .catch((err) => {
        if (!active) return;
        setResult((prev) => ({ ...prev, loading: false }));
        toastError(err);
      });
    return () => {
      active = false;
    };
  }, [open, term]);

  // A escolhida entra nas opções mesmo fora da página da busca.
  const options =
    value && !result.members.some((member) => member.id === value.id) ? [value, ...result.members] : result.members;
  const hint =
    result.total > result.members.length ? t("history.morePeople", { shown: result.members.length, total: result.total }) : null;

  // O aviso de que há mais pessoas, no pé da lista.
  const PickerPaper = useMemo(
    () =>
      function PickerPaperWithHint({ children, ...props }) {
        return (
          <Paper {...props}>
            {children}
            {hint && <div className={classes.pickerHint}>{hint}</div>}
          </Paper>
        );
      },
    [hint, classes.pickerHint]
  );

  return (
    <Autocomplete
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      options={options}
      value={value}
      onChange={(_event, chosen) => onChange(chosen)}
      inputValue={text}
      onInputChange={(_event, next) => setText(next)}
      // O servidor já filtrou.
      filterOptions={(list) => list}
      getOptionLabel={(option) => option.name || ""}
      getOptionSelected={(option, chosen) => option.id === chosen.id}
      loading={result.loading}
      loadingText={t("history.loadingPeople")}
      noOptionsText={t("history.noPeople")}
      PaperComponent={PickerPaper}
      classes={{ paper: classes.pickerPaper, option: classes.pickerOption }}
      ListboxProps={{ style: { maxHeight: 320 } }}
      className={classes.personPicker}
      renderOption={(option) => (
        <span className={classes.pickerPerson}>
          <PersonAvatar name={option.name} image={option.profileImage} small />
          <span className={classes.nameWrap}>
            <span className={classes.pickerName}>{option.name}</span>
            {option.email && <span className={classes.email}>{option.email}</span>}
          </span>
        </span>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          size="small"
          variant="outlined"
          label={t("history.person")}
          placeholder={t("history.allPeople")}
          InputLabelProps={{ ...params.InputLabelProps, shrink: true }}
          InputProps={{
            ...params.InputProps,
            startAdornment: value ? (
              <span className={classes.pickerInputAvatar}>
                <PersonAvatar name={value.name} image={value.profileImage} small />
              </span>
            ) : (
              params.InputProps.startAdornment
            ),
          }}
          className={classes.dateField}
        />
      )}
    />
  );
};

export default PersonFilter;
